import { useState, useRef, useEffect } from "react";
import type { ReactNode } from "react";
import { NavLink, Link, useNavigate, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api.js";
import { useAuth } from "../lib/AuthContext.js";
import { initialsOf } from "../lib/initials.js";
import {
  Grid,
  Briefcase,
  Users,
  Video,
  Coins,
  Link2,
  ChevronDown,
  User,
  ShieldCheck,
  LogOut,
  Check,
  Copy,
  Mail,
} from "./icons.js";
import { ImpersonationBanner } from "./ImpersonationBanner.js";
import { FeedbackButton } from "./FeedbackButton.js";
import { NotificationBell } from "../personas/users/NotificationBell.js";
import { useTheme } from "../lib/theme.js";
import { setDashboardView } from "../personas/users/DashboardHome.js";
import { Moon, Sun, Network, Compass, Plus, UserPlus } from "lucide-react";

interface InboxCountsResponse {
  counts: { total: number; needs_review: number; unassigned: number };
}

export function CustomerShell({ children }: { children: ReactNode }): ReactNode {
  const { me, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [profileOpen, setProfileOpen] = useState(false);
  const profileRef = useRef<HTMLDivElement>(null);


  // Inbox attention count — emailed applications that could not be filed, or that matched no
  // job. Deliberately NOT the inbox total: a badge is a call to action, and a message already
  // filed as a candidate needs nothing from anyone.
  const { data: inboxData } = useQuery({
    queryKey: ["inbox-counts", me?.orgId],
    queryFn: () => api.get<InboxCountsResponse>(`/orgs/${me!.orgId}/inbound-emails?limit=1`),
    enabled: !!me?.orgId,
    staleTime: 30_000,
  });

  // NAV BADGES ARE CALLS TO ACTION, OR NOTHING.
  //
  // Only the Inbox carries one: messages that could not be filed or matched no job, i.e. work
  // waiting for someone. Requirement / Candidates / Interview used to show totals (active roles,
  // every in-flight candidate on ANY role — closed and on-hold ones included — and interviews
  // verified this month): three different kinds of number, none of them "something needs you",
  // and uncapped on desktop. Totals live on the Dashboard.
  //
  // `undefined` while the query is in flight (rendered as no badge at all, below) rather than 0:
  // flashing a confident zero before the data lands is its own small lie.
  const inboxAttention = inboxData
    ? inboxData.counts.needs_review + inboxData.counts.unassigned
    : undefined;

  // Close profile dropdown on outside click
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (profileRef.current && !profileRef.current.contains(e.target as Node)) {
        setProfileOpen(false);
      }
    }
    if (profileOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [profileOpen]);

  const userInitial = initialsOf(me?.name, me?.email || "C").slice(0, 1) || "C";

  // Dark / Light: the app-wide theme (lib/theme — same [data-theme] switch every page reads).
  // "system" follows the OS, so resolve it to know which way the toggle should flip.
  const { theme, set: setTheme } = useTheme();
  const [systemDark, setSystemDark] = useState(() => window.matchMedia("(prefers-color-scheme: dark)").matches);
  useEffect(() => {
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = (e: MediaQueryListEvent): void => setSystemDark(e.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  const isDark = theme === "dark" || (theme === "system" && systemDark);
  const onNetworkView = location.pathname.startsWith("/app/network");

  const navItems = [
    // /app opens the saved view (DashboardHome) — Network View counts as "Dashboard" too.
    { to: "/app", end: true, label: "Dashboard", short: "Home", icon: Grid, badge: undefined, alsoActive: onNetworkView },
    { to: "/app/requisitions", label: "Requirement", short: "Jobs", icon: Briefcase },
    {
      to: "/app/candidates",
      label: "Candidates",
      short: "Candidates",
      icon: Users,
      alsoActive: location.pathname.startsWith("/app/resumes"),
    },
    {
      to: "/app/inbox",
      label: "Inbox",
      short: "Inbox",
      icon: Mail,
      badge: inboxAttention,
      title: "Applications received by email, and what was done with each one",
    },
    { to: "/app/interviews", label: "Interview", short: "Interviews", icon: Video },
  ] as {
    to: string;
    end?: boolean;
    label: string;
    short: string;
    icon: typeof Grid;
    badge?: number;
    alsoActive?: boolean;
    title?: string;
  }[];

  // Secondary destinations that used to live only in the profile menu — on the rail they are one
  // click away (they stay in the menu too, for phones where the rail is a bottom bar).
  const railExtras = [
    { to: "/app/team", label: "Team", icon: UserPlus },
    { to: "/app/connections", label: "Connect", icon: Link2 },
    { to: "/app/plans", label: "Plans", icon: Coins },
  ];

  return (
    <div className="customer-app-layout">
      {/* Left icon rail (desktop/tablet). Phones get the same hubs as a bottom tab bar, below. */}
      <nav className="customer-rail" aria-label="Main">
        <Link to="/app" className="customer-logo-mark" aria-label="TalentHop home">TH</Link>
        {navItems.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            data-tip={item.title}
            data-tip-pos="right"
            className={({ isActive }) => `customer-rail-item ${isActive || item.alsoActive ? "active" : ""}`}
          >
            <item.icon />
            <span>{item.short}</span>
            {item.badge ? <span className="customer-rail-badge">{item.badge > 99 ? "99+" : item.badge}</span> : null}
          </NavLink>
        ))}
        <div className="customer-rail-sep" aria-hidden="true" />
        {railExtras.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            className={({ isActive }) => `customer-rail-item ${isActive ? "active" : ""}`}
          >
            <item.icon />
            <span>{item.label}</span>
          </NavLink>
        ))}
      </nav>

      <div className="customer-column">
      <header className="customer-topbar">
        <div className="customer-topbar-inner">
          <Link to="/app" className="customer-brand">
            <div className="customer-logo-mark">TH</div>
            <div className="customer-brand-text">
              <span className="customer-brand-title">TalentHop</span>
              <span className="customer-brand-sub">AI Recruiter Platform</span>
            </div>
          </Link>

          {/* Right Action Controls */}
          <div className="customer-actions">
            <Link to="/app/new" className="customer-btn-new">
              <span className="label">New requirement</span>
              <span className="plus" aria-hidden="true"><Plus /></span>
            </Link>
            {/* "We Source" is no longer a top-bar button (#236): it is a choice on the New
                requirement page itself (We source / I'll source). /app/new?mode=source still works. */}

            <NotificationBell />

            <FeedbackButton variant="customer" />

            <button
              type="button"
              className="customer-feedback-btn"
              onClick={() => setTheme(isDark ? "light" : "dark")}
              data-tip={isDark ? "Light mode" : "Dark mode"}
              aria-label={isDark ? "Switch to light mode" : "Switch to dark mode"}
            >
              {isDark ? <Sun className="btn-icon" /> : <Moon className="btn-icon" />}
            </button>

            {/* Profile Dropdown */}
            <div className="customer-profile-container" ref={profileRef}>
              <button
                type="button"
                className="customer-profile-btn"
                onClick={() => setProfileOpen((v) => !v)}
                aria-expanded={profileOpen}
                aria-label="User Profile Menu"
                data-tip="Account"
              >
                <span className="customer-avatar">{userInitial}</span>
                <ChevronDown className="customer-profile-arrow" />
              </button>

              {profileOpen && (
                <div className="customer-profile-menu">
                  <div className="customer-profile-header">
                    <div className="customer-profile-name">{me?.name || "Hiring Manager"}</div>
                    <div className="customer-profile-email">{me?.email || ""}</div>
                  </div>
                  <div className="customer-profile-divider" />
                  {/* View switch — Classic stays the /app home; Network View is the graph at /app/network. */}
                  <Link
                    to="/app"
                    className="customer-menu-item"
                    aria-current={onNetworkView ? undefined : "page"}
                    onClick={() => {
                      setDashboardView("classic"); // before navigating — /app reads it on arrival
                      setProfileOpen(false);
                    }}
                  >
                    <Grid className="menu-icon" />
                    <span>Classic Dashboard</span>
                    {!onNetworkView && <Check className="menu-icon" />}
                  </Link>
                  <Link
                    to="/app/network"
                    className="customer-menu-item desktop-only"
                    aria-current={onNetworkView ? "page" : undefined}
                    onClick={() => {
                      setDashboardView("network");
                      setProfileOpen(false);
                    }}
                  >
                    <Network className="menu-icon" />
                    <span>Network View</span>
                    {onNetworkView && <Check className="menu-icon" />}
                  </Link>
                  {/* Reopens the Network walkthrough (NetworkTour.tsx) — it also opens itself on first visit. */}
                  <Link
                    to="/app/network?tour=1"
                    className="customer-menu-item desktop-only"
                    onClick={() => setProfileOpen(false)}
                  >
                    <Compass className="menu-icon" />
                    <span>Getting started guide</span>
                  </Link>
                  <div className="customer-profile-divider" />
                  <Link
                    to="/app/team"
                    className="customer-menu-item"
                    onClick={() => setProfileOpen(false)}
                  >
                    <Users className="menu-icon" />
                    <span>User Roles</span>
                  </Link>
                  <Link
                    to="/app/connections"
                    className="customer-menu-item"
                    onClick={() => setProfileOpen(false)}
                  >
                    <Link2 className="menu-icon" />
                    <span>Connections</span>
                  </Link>
                  <Link
                    to="/app/plans"
                    className="customer-menu-item"
                    onClick={() => setProfileOpen(false)}
                  >
                    <Coins className="menu-icon" />
                    <span>Plans & usage</span>
                  </Link>
                  <Link
                    to="/app/security"
                    className="customer-menu-item"
                    onClick={() => setProfileOpen(false)}
                  >
                    <ShieldCheck className="menu-icon" />
                    <span>Account Settings</span>
                  </Link>
                  <div className="customer-profile-divider" />
                  <button
                    type="button"
                    className="customer-menu-item logout"
                    onClick={() => {
                      setProfileOpen(false);
                      logout?.();
                    }}
                  >
                    <LogOut className="menu-icon" />
                    <span>Sign Out</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      <ImpersonationBanner />

      {/* Main Content Area */}
      <main className="customer-main-area">{children}</main>
      </div>

      {/* Phones: the primary hubs as a bottom tab bar (the rail is hidden at ≤900px — see app.css). */}
      <nav className="customer-bottom-nav" aria-label="Main">
        {navItems.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            className={({ isActive }) => `customer-bottom-item ${isActive || item.alsoActive ? "active" : ""}`}
          >
            <span className="customer-bottom-icon">
              <item.icon />
              {item.badge ? <span className="customer-bottom-badge">{item.badge > 99 ? "99+" : item.badge}</span> : null}
            </span>
            <span className="customer-bottom-label">{item.short}</span>
          </NavLink>
        ))}
      </nav>

      {/* Connectivity Modal */}
    </div>
  );
}
